import { Alert } from "@jugl-web/ui-components";
import { useTranslations } from "@jugl-web/utils";
import React, { useCallback, useEffect, useState, ReactNode } from "react";
import { Lottie } from "@jugl-web/utils/utils/Lottie";
import { useEntitiesListProvider } from "@web-src/features/entities/providers/EntitiesListProvider/useEntitiesListProvider";
import { EntityModel } from "@jugl-web/rest-api";
import { DeleteEntityConfirmationDialog } from "@web-src/modules/entities/components/DeleteEntityConfirmationDialog";
import { useConfirmationDialogState } from "@jugl-web/utils/hooks/useConfirmationDialogState";
import { EntitiesListProvider } from "@web-src/features/entities/providers/EntitiesListProvider/EntitiesListProvider";
import { useNavigation } from "@web-src/modules/navigation/hooks/useNavigation";
import alertAnimation from "./assets/alert-animation.json";
import { EntityTile } from "./components/EntityTile";

type ManageWorkspaceAdminsDialogProps = {
  isOpen: boolean;
  entityIds?: string[];
  onDelete: () => void;
  onClose: () => void;
};

const ManageWorkspaceAdminsDialogContent: React.FC<
  ManageWorkspaceAdminsDialogProps
> = ({ isOpen, onClose, entityIds, onDelete }) => {
  const { t } = useTranslations();
  const { navigateToPage } = useNavigation();
  const [matchedEntities, setMatchedEntities] = useState<EntityModel[]>([]);
  const deleteEntityConfirmationDialogState = useConfirmationDialogState<{
    entity: EntityModel;
  }>();

  const {
    entitiesState: { entities, isFetching },
  } = useEntitiesListProvider();

  useEffect(() => {
    if (isOpen && !isFetching && entities?.length && entityIds?.length) {
      const found = entities.filter((e) => entityIds.includes(e.id));

      setMatchedEntities(found);
    }
  }, [isFetching, entities, entityIds, isOpen]);

  const handleDeleteEntity = useCallback(() => {
    if (!isOpen || !deleteEntityConfirmationDialogState.metadata?.entity)
      return;
    const filteredEntities = matchedEntities.filter(
      (entity) =>
        entity.id !== deleteEntityConfirmationDialogState.metadata?.entity.id
    );
    setMatchedEntities(filteredEntities);
    if (!filteredEntities.length) {
      onDelete();
    }
  }, [
    isOpen,
    deleteEntityConfirmationDialogState.metadata?.entity,
    matchedEntities,
    onDelete,
  ]);

  const handleClose = () => {
    onClose();
    setMatchedEntities([]);
  };

  return (
    <>
      <Alert
        isOpen={isOpen && !deleteEntityConfirmationDialogState.isOpen}
        headerClassName="border-0 justify-end"
        isCloseButtonVisible
        img={
          <Lottie
            options={{ animationData: alertAnimation }}
            height={70}
            width={70}
          />
        }
        onRequestClose={handleClose}
        title={t({
          id: "manage-workspace-admins-dialog-component.manage-workspace-admins-title",
          defaultMessage: "Manage Workspace Admins",
        })}
        content={
          <div className="flex flex-col gap-10">
            <span>
              {matchedEntities.length > 1
                ? t(
                    {
                      id: "manage-workspace-admins-dialog-component.manage-workspace-admins-description",
                      defaultMessage:
                        "You are the single Admin of {count} {count, plural, one {Workspace} other {Workspaces}}. Before you leave, please transfer Admin role for someone to take care of the Workspaces",
                    },
                    { count: matchedEntities.length }
                  )
                : t(
                    {
                      id: "manage-workspace-admins-dialog-component.manage-workspace-admins-description-single",
                      defaultMessage:
                        "You are the single Admin of <b>{entityName}</b>. Before you leave, please <i>transfer Admin role</i> for someone to take care of the Workspace",
                    },
                    {
                      entityName: matchedEntities[0]?.name,
                      b: (text: ReactNode) => (
                        <span className="font-semibold text-primary-800">
                          {text}
                        </span>
                      ),
                      i: (text: ReactNode) => (
                        <span className="italic underline">{text}</span>
                      ),
                    }
                  )}
            </span>
            <div className="flex flex-col gap-4">
              {matchedEntities.map((entity) => (
                <EntityTile
                  key={entity.id}
                  entity={entity}
                  onDelete={() =>
                    deleteEntityConfirmationDialogState.open({
                      metadata: { entity },
                    })
                  }
                  onManageAdmins={() => {
                    navigateToPage("workspaceMembers", undefined, {
                      entityId: entity.id,
                    });
                    handleClose();
                  }}
                />
              ))}
            </div>
          </div>
        }
      />
      <DeleteEntityConfirmationDialog
        isOpen={deleteEntityConfirmationDialogState.isOpen}
        entity={
          deleteEntityConfirmationDialogState.metadata?.entity as EntityModel
        }
        onEntityDeleted={handleDeleteEntity}
        onClose={deleteEntityConfirmationDialogState.close}
      />
    </>
  );
};

export const ManageWorkspaceAdminsDialog: React.FC<
  ManageWorkspaceAdminsDialogProps
> = (props) => (
  <EntitiesListProvider>
    <ManageWorkspaceAdminsDialogContent {...props} />
  </EntitiesListProvider>
);
